import { Component, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AuthService } from '../../services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { ClimateToggleComponent } from '../../../../theme/climate-toggle.component';
import { PalettePickerComponent } from '../../../../theme/palette-picker.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SNACKBAR_DURATION, APP_VERSION } from '../../../constants';
import { finalize } from 'rxjs';

const materialModules = [
  FormsModule,
  CommonModule,
  MatCardModule,
  MatInputModule,
  MatButtonModule,
  MatFormFieldModule,
  MatIconModule,
  ClimateToggleComponent,
  PalettePickerComponent,
  MatDividerModule,
  NgOptimizedImage,
];

@Component({
  selector: 'nt-register',
  standalone: true,
  imports: [materialModules],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent {
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly SYSTEM_TAG = 'WEB-SERVICE';
  readonly MAX_RETRY_COUNTER = 5;

  public readonly version: string = APP_VERSION;
  name: string = '';
  email: string = '';
  phone: string = '';
  rfc: string = '';
  verificationCode: number | null = null;
  loading: boolean = false;
  step: number = 1;
  retryCounter: number = 0;
  notificationChannel: string = 'WhatsApp';
  lastRegisterMessage: string = '';
  year: number = new Date().getFullYear();

  register(form: NgForm): void {
    if (form.invalid) {
      form.control.markAllAsTouched();
      return;
    }

    this.sendRegisterRequest(0, false);
  }

  resendVerificationCode(): void {
    if (this.loading) {
      return;
    }

    if (this.retryCounter >= this.MAX_RETRY_COUNTER) {
      this._snackBar.open(
        'Se alcanzó el límite de reenvíos permitidos.',
        'Cerrar',
        { duration: SNACKBAR_DURATION, panelClass: ['snackbar-error'] }
      );
      this._cdr.markForCheck();
      return;
    }

    this.sendRegisterRequest(this.retryCounter + 1, true);
  }

  private sendRegisterRequest(retryCounter: number, isResend: boolean): void {
    this.loading = true;
    this._authService
      .$register({
        name: this.name,
        email: this.email,
        phone: this.phone,
        rfc: this.rfc,
        systemTag: this.SYSTEM_TAG,
        retryCounter,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this._cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.retryCounter = retryCounter;
          this.step = 2;
          this.lastRegisterMessage = res.message || '';
          this.notificationChannel = this.resolveNotificationChannel(res.message);
          this._cdr.markForCheck();
          this._snackBar.open(
            `${isResend ? 'Código reenviado.' : 'Registro exitoso.'} ${res.message || `Se ha enviado el código de verificación a: ${res.email}`}`,
            'Cerrar',
            { duration: SNACKBAR_DURATION, panelClass: ['snackbar-success'] }
          );
        },
        error: (error) => {
          this._snackBar.open(
            'Error al registrar: ' + (error?.error?.message || error?.message || 'Error inesperado'),
            'Cerrar',
            { duration: SNACKBAR_DURATION, panelClass: ['snackbar-error'] }
          );
          this._cdr.markForCheck();
        },
      });
  }

  private resolveNotificationChannel(message?: string): string {
    if (!message) {
      return this.retryCounter > 2 ? 'Correo' : 'WhatsApp';
    }

    if (message.toLowerCase().includes('whatsapp')) {
      return 'WhatsApp';
    }

    if (message.toLowerCase().includes('correo')) {
      return 'Correo';
    }

    return this.retryCounter > 2 ? 'Correo' : 'WhatsApp';
  }

  verifyCode(): void {
    if (!this.verificationCode) {
      this._snackBar.open('Por favor ingrese el código de verificación', 'Cerrar', { duration: SNACKBAR_DURATION, panelClass: ['snackbar-error'] });
      return;
    }
    this.loading = true;
    this._authService.$verifyCode({
      email: this.email,
      verificationCode: this.verificationCode,
      message: '',
    }).pipe(
      finalize(() => {
        this.loading = false;
        this._cdr.markForCheck();
      })
    ).subscribe({
      next: () => {
        this._snackBar.open('Cuenta verificada exitosamente. Por favor inicie sesión.', 'Cerrar', { duration: SNACKBAR_DURATION, panelClass: ['snackbar-success'] });
        this._router.navigateByUrl('/login');
      },
      error: (error) => {
        console.error('Error verifying code:', error);
        this._snackBar.open('Error al verificar código: ' + (error?.message || 'Código inválido'), 'Cerrar', { duration: SNACKBAR_DURATION, panelClass: ['snackbar-error'] });
        this._cdr.markForCheck();
      },
    });
  }
}
